/**
 * SPEC-AgentUI / SPEC-AgentDetail-Revisions — ClaimSubscriptionProvider.
 *
 * Lives at the app root. Subscribes to the SPEC-q-claim `qa:claim:warning`
 * point-to-point event and turns it into a single off-page "Expiring Soon"
 * toast. The 2026-05-29 Agent Detail revisions collapsed the surface:
 *   - One toast variant (warn). The success / released / crit variants are
 *     deleted from the toast component.
 *   - Title shape: "Delivery <Customer> › <Recipient> — Expiring Soon".
 *     Customer + recipient resolved client-side from the TanStack Query cache
 *     (the unified delivery key `["qa-delivery", id]`); when the agent has
 *     never opened the delivery page in the session there's nothing in cache
 *     and the title falls back to the deliveryId.
 *   - Single primary button: "↩ Click to Return" — navigates to the unified
 *     QA detail route. No "Let it go" secondary.
 *   - The on-page TimerChip suppression (when the agent is already viewing
 *     the matching delivery) stays. The inline chip is the on-page surface.
 *   - No success toast on extend (the TimerChip reset confirms).
 *   - No "released" toast on auto-release — claim quietly returns to queue
 *     and the agent discovers loss on their next glance.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";
import { useQaWebSocket } from "@/hooks/use-websocket";
import { ClaimToast, type ClaimToastVariant } from "./agent-claim-toast";
import {
  isClaimSuppressed,
  type MinimalStorage,
} from "@/lib/agent-ui-helpers";

interface ActiveToast {
  key: string;
  variant: ClaimToastVariant;
  title: string;
  subtitle?: string;
  claimId?: string;
  primary?: { label: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
  autoDismissMs?: number;
}

interface ClaimSubscriptionContextValue {
  pushToast: (toast: Omit<ActiveToast, "key">) => void;
  dismissToast: (key: string) => void;
}

const ClaimSubscriptionContext = createContext<ClaimSubscriptionContextValue | null>(null);

export function useClaimSubscription(): ClaimSubscriptionContextValue {
  const ctx = useContext(ClaimSubscriptionContext);
  if (!ctx) {
    return {
      pushToast: () => {},
      dismissToast: () => {},
    };
  }
  return ctx;
}

/**
 * Best-effort lookup of customer + recipientName for a given deliveryId from
 * the TanStack Query cache. Reads the unified ["qa-delivery", id] key per
 * SPEC-UnifiedQADetail. If the agent has never opened the delivery in this
 * session the cache will be empty and we return nulls — the toast title
 * falls back to the deliveryId.
 */
function readDeliveryMeta(
  queryClient: ReturnType<typeof useQueryClient>,
  deliveryId: string,
): { customer: string | null; recipientName: string | null } {
  const data = queryClient.getQueryData<any>(["qa-delivery", deliveryId]);
  if (!data) return { customer: null, recipientName: null };
  const customer = data?.agentView?.customer ?? null;
  const recipientName = data?.recipientName ?? null;
  return { customer, recipientName };
}

function buildExpiringTitle(customer: string | null, recipientName: string | null, deliveryId: string): string {
  const cust = (customer ?? "").trim();
  const recp = (recipientName ?? "").trim();
  if (cust && recp) return `Delivery ${cust} › ${recp} — Expiring Soon`;
  if (cust) return `Delivery ${cust} — Expiring Soon`;
  if (recp) return `Delivery ${recp} — Expiring Soon`;
  return `Delivery ${deliveryId} — Expiring Soon`;
}

export function AgentClaimSubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { isAgent, userId, name, email } = useRole();
  const { user } = useAuth();
  const [location, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const seqRef = useRef(0);

  const storage: MinimalStorage = useMemo(() => {
    if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
      const fallback = new Map<string, string>();
      return {
        getItem: (k) => fallback.get(k) ?? null,
        setItem: (k, v) => { fallback.set(k, v); },
      };
    }
    return window.sessionStorage;
  }, []);

  const dismissToast = useCallback((key: string) => {
    setToasts((t) => t.filter((x) => x.key !== key));
  }, []);

  const pushToast = useCallback((toast: Omit<ActiveToast, "key">) => {
    const key = `agent-claim-toast-${++seqRef.current}`;
    setToasts((t) => [
      ...t.filter((x) => x.claimId !== toast.claimId || x.variant !== toast.variant),
      { ...toast, key },
    ]);
  }, []);

  const ctxValue = useMemo<ClaimSubscriptionContextValue>(
    () => ({ pushToast, dismissToast }),
    [pushToast, dismissToast],
  );

  function currentDeliveryIdFromLocation(): string | null {
    // SPEC-UnifiedQADetail Phase 2 — unified detail route.
    const m = location.match(/^\/qa\/delivery\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  useQaWebSocket(
    isAgent ? userId : null,
    () => {},
    { userName: name ?? user?.email ?? "", email: email ?? "" },
    {
      onClaimWarning: (e) => {
        if (!isAgent) return;
        if (isClaimSuppressed(e.claimId, storage)) return;
        const onPage = currentDeliveryIdFromLocation() === e.deliveryId;
        if (onPage) {
          // On-page inline TimerChip handles the surface. No toast.
          return;
        }
        const { customer, recipientName } = readDeliveryMeta(queryClient, e.deliveryId);
        pushToast({
          variant: "warn",
          claimId: e.claimId,
          title: buildExpiringTitle(customer, recipientName, e.deliveryId),
          primary: {
            label: "↩ Click to Return",
            onClick: () => {
              // Navigate to the unified QA detail route — the inline TimerChip
              // takes over from there.
              navigate(`/qa/delivery/${e.deliveryId}`);
            },
          },
          // No secondary — the SPEC dropped "Let it go". Dismiss (×) closes
          // the toast but does NOT extend or release; the clock keeps running.
        });
      },
      // SPEC-AgentDetail-Revisions — onClaimAutoReleased is no longer
      // surfaced as a toast. Agents discover claim loss on their next queue
      // glance, not via an announcement.
    },
  );

  // Drop stale warn toasts when the user navigates to the matching delivery
  // (the inline chip takes over).
  useEffect(() => {
    const here = currentDeliveryIdFromLocation();
    if (!here) return;
    setToasts((t) => t.filter((x) => !x.claimId || x.variant !== "warn"));
  }, [location]);

  return (
    <ClaimSubscriptionContext.Provider value={ctxValue}>
      {children}
      {isAgent &&
        toasts.map((t) => (
          <ClaimToast
            key={t.key}
            variant={t.variant}
            title={t.title}
            subtitle={t.subtitle}
            primary={t.primary}
            secondary={t.secondary}
            onDismiss={() => dismissToast(t.key)}
            autoDismissMs={t.autoDismissMs}
            testId={`agent-claim-toast-${t.variant}`}
          />
        ))}
    </ClaimSubscriptionContext.Provider>
  );
}
